import React, {ReactNode, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import clsx from 'clsx';
import {tid} from '../../../../../../testUtils';
import FileTreeAntd from '../../../../../core/FileTree/FileTreeAntd';
import {PageFirst16} from '@carbon/icons-react';
import {
  ModelFileTags,
  ModelFileUploaded,
  ModelTreeNode,
  TreeNode,
  useModelFileUploader,
  useModelFileTreeTHLoader,
} from '../../../../../../store/modelFile';
import DialogConfirmation from '../../../../../core/dialog/DialogConfirmation';
import {calculateFilePath, ModelEditFilesMode, onDocumentDragOver} from './utils';
import {calculateFileName, nodeHasTag} from '../../../../../../store/modelFile/utils';
import {DeleteOutlined} from '@ant-design/icons';
import {ModelDTO} from 'hemwb-api';
import {Form, useForm} from '../../../../../core/Form';
import {FormGroup} from 'react-reactive-form';
import {
  isModelExternal,
  isModelWeb,
  isModelTheorem,
  isModelPneuInsights,
  isModelGlobal,
} from '../../../../../../store/model';
import {Checkbox, Button, Tabs} from 'antd';
import FileTagSelectionBox from './FileTagSelectionBox';
import FileTagSelectionBoxAntd from './FileTagSelectionBoxAntd';
import FileTreeTH from '../../../../../core/FileTree/FileTreeTH';
import stylesAntd from './FileManagerAntd.module.scss';

type PropertySelection = {[uuid: string]: boolean};

export type FileManagerAntdHooks = {
  add: (file: File, path: string) => void;
  nodes: ModelTreeNode[];
  handleAddFiles: (event: React.FormEvent<HTMLInputElement>) => void;
  handleDrop: (event: React.DragEvent, node: TreeNode) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  folderInputRef: React.RefObject<HTMLInputElement>;
  isDirUploadAvailable: boolean;
  selectedCount: number;
  renderFileTagSelection: () => React.ReactNode;
  renderFileTree: () => React.ReactNode;
};

type FileManagerAntdProps = {
  model: ModelDTO;
  files?: ModelTreeNode[];
  mode: ModelEditFilesMode;
  onEdit: () => void;
  onChange?: (updatedModel: ModelDTO, updatedFiles: ModelTreeNode[]) => void;
  onSubmit: (updatedModel: ModelDTO, updatedFiles: ModelTreeNode[]) => void;
  buttons: () => string | ReactNode;
  createFlow?: boolean;
  uploadFlow?: boolean;
  cloneFlow?: boolean;
  unSelectAll?: boolean;
  hideUploadFileButton?: boolean;
  hideDeleteFileButton?: boolean;
  filesSelection?: PropertySelection;
  filteredFiles?: boolean;
  customUploadArea?: (hooks: FileManagerAntdHooks) => ReactNode;
};

const FileManagerAntd: React.FC<FileManagerAntdProps> = ({
  buttons,
  createFlow,
  uploadFlow,
  cloneFlow,
  onSubmit,
  onChange,
  onEdit,
  model,
  files,
  //mode,
  //hideUploadFileButton,
  hideDeleteFileButton,
  filteredFiles,
  customUploadArea,
}) => {
  const {add, markDelete, unmarkDelete, remove, markForFileTag, uploader} = useModelFileUploader(model, files);
  const filteredFileUuids = new Set(files?.map((file) => file.uuid));
  const nodes =
    files && filteredFiles
      ? uploader.nodes.filter((node) => filteredFileUuids.has(node.uuid) || !node.uploaded)
      : uploader.nodes;
  const [initialized, setInitialized] = useState(false);
  const externalModel = isModelExternal(model);
  const webModel = isModelWeb(model);
  const theoremModel = isModelTheorem(model);
  const globalModel = isModelGlobal(model);
  const pneuInsightsModel = isModelPneuInsights(model);
  const [selected, setSelected] = useState<PropertySelection>({});
  const [selectedCount, setSelectedCount] = useState(0);
  const [unSelectAll, setUnSelectAll] = useState(false);
  const [tab, setTab] = useState('theorem');
  const {data: THfiles, load: loadFiles} = useModelFileTreeTHLoader(model.uuid);

  useEffect(() => {
    if (theoremModel && tab === 'theorem') {
      loadFiles();
    }
  }, [tab, loadFiles, theoremModel]);

  useEffect(() => {
    const newSelected = {...selected};
    for (const node of nodes) {
      if (getNodeIdentifier(node) in newSelected) continue;
      newSelected[getNodeIdentifier(node)] = false;
    }
    setSelected(newSelected);
  }, []);

  const setSpecificFiles = useCallback(
    (uuids: string[], checked: boolean) => {
      const newSelected = {...selected};
      for (const uuid of uuids) {
        newSelected[uuid] = checked;
      }
      setSelected(newSelected);
      const selectedFilesCount = newSelected ? Object.values(newSelected).filter((value) => value).length : 0;
      setSelectedCount(selectedFilesCount);
      // Reset unSelectAll when Select All is used
      if (checked) {
        setUnSelectAll(false);
      }
    },
    [selected],
  );

  const unSelectAllFiles = () => {
    for (const uuid in selected) {
      selected[uuid] = false;
    }
    setSelectedCount(0);
    setUnSelectAll(true);
  };

  useEffect(() => {
    setInitialized(true);
  }, []);

  useEffect(() => {
    if (initialized) {
      onChange?.(model, nodes);
    }
  }, [nodes, initialized]);

  useEffect(() => {
    document.addEventListener('dragover', onDocumentDragOver, false);
    document.addEventListener('drop', onDocumentDragOver, false);
    return () => {
      document.removeEventListener('dragover', onDocumentDragOver);
      document.removeEventListener('drop', onDocumentDragOver, false);
    };
  }, []);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const isDirUploadAvailable = useMemo(() => {
    const input = document.createElement('input');
    return typeof input.webkitdirectory === 'boolean';
  }, []);

  const dispatchAddFile = useCallback(
    (file: File, node: TreeNode | string) => {
      let path;
      if (typeof node === 'string') {
        path = node;
      } else {
        const pathName = `${node.path}/`;
        path = `${node.path === '' ? '' : pathName}${calculateFileName(file.name)}`;
      }

      add(file, path);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (folderInputRef.current) {
        folderInputRef.current.value = '';
      }
    },
    [add],
  );

  const handleAddFiles = useCallback(
    (event: React.FormEvent<HTMLInputElement>) => {
      onEdit();
      if (event.currentTarget.files) {
        Array.from(event.currentTarget.files).forEach((file: File) => {
          const path = calculateFilePath(calculateFileName(file.name), file.webkitRelativePath);
          dispatchAddFile(file, path);
        });
      }
    },
    [dispatchAddFile, onEdit],
  );

  const [confirmNodeDeletion, setConfirmNodeDeletion] = useState<ModelFileUploaded | null>(null);
  const handleDeleteFile = useCallback(
    (node: ModelFileUploaded) => {
      if (node.mainFile) {
        setConfirmNodeDeletion(node);
      } else {
        markDelete(node);
      }
    },
    [markDelete],
  );
  const handleConfirmDeleteFile = useCallback(() => {
    if (confirmNodeDeletion) {
      markDelete(confirmNodeDeletion);
    }
    setConfirmNodeDeletion(null);
  }, [markDelete, confirmNodeDeletion]);

  const handleDeclineDeleteFile = useCallback(() => {
    setConfirmNodeDeletion(null);
  }, []);

  const handleSaveFileTag = useCallback(
    (fileTag: string) => {
      for (const node of nodes) {
        if (selected[getNodeIdentifier(node)]) {
          markForFileTag(node, fileTag);
          selected[getNodeIdentifier(node)] = false;
        }
        setUnSelectAll(true);
        setSelectedCount(0);
      }
    },
    [markForFileTag, selectedCount, setUnSelectAll, selected],
  );

  const getNodeIdentifier = (node: ModelTreeNode) => (node.uuid ? node.uuid : node.path);

  const handleFileTag = useCallback(
    (node: ModelFileUploaded, fileTag: string) => {
      markForFileTag(node, fileTag);
    },
    [markForFileTag],
  );

  const handleDrop = useCallback(
    (event: React.DragEvent, node: TreeNode) => {
      if (!node || !node.folder || pneuInsightsModel) {
        return;
      }

      function traverseFileTree(item: any, p?: string) {
        const path = p || '';
        if (item.isFile) {
          // Get file
          item.file(function (file: File) {
            const newPath = `${node.path}${node.path ? '/' : ''}${path}${file.name}`;
            dispatchAddFile(file, newPath);
          });
        } else if (item.isDirectory) {
          // Get folder contents
          const dirReader = item.createReader();
          dirReader.readEntries(function (entries: any[]) {
            for (const entry of entries) {
              traverseFileTree(entry, path + item.name + '/');
            }
          });
        }
      }

      if (event.dataTransfer.items) {
        // Use DataTransferItemList interface to access the file(s)
        for (const item of event.dataTransfer.items) {
          // If dropped items aren't files, reject them
          if (item.kind === 'file') {
            const entry = item.webkitGetAsEntry();
            if (entry) {
              traverseFileTree(entry);
            } else {
              const file = item.getAsFile();
              if (file) {
                dispatchAddFile(file, node);
              }
            }
          }
        }
      } else {
        // Use DataTransfer interface to access the file(s) - Fallback for older browsers like IE11
        for (const file of event.dataTransfer.files) {
          dispatchAddFile(file, node);
        }
      }
    },
    [dispatchAddFile, pneuInsightsModel],
  );

  const renderButtons = useCallback(
    (node: TreeNode) => {
      if (node.folder || (nodeHasTag(node, ModelFileTags.BITBUCKET_FILE) && !node.uploaded)) {
        return null;
      }

      if (nodeHasTag(node, ModelFileTags.PARSING)) {
        return [<span key={`progress-${node.uuid}`}>Parsing...</span>];
      }

      return [
        node.uploaded &&
          !nodeHasTag(node, ModelFileTags.DELETE) &&
          !nodeHasTag(node, ModelFileTags.PUBLISHED_MAIN_FILE) &&
          !nodeHasTag(node, ModelFileTags.PUBLISHED_MODEL_FILE) &&
          !pneuInsightsModel && (
            <div className={stylesAntd.buttonContainer} key={`uploaded-actions-${node.uuid}`}>
              <div className={stylesAntd.checkBoxContainer}>
                <Checkbox
                  key={`selected-${node.uuid}`}
                  checked={selected[node.uuid] || false}
                  onChange={(event) => {
                    const isChecked = event.target.checked;
                    // If unchecking any file, trigger unSelectAll to notify parent
                    if (!isChecked) {
                      setUnSelectAll(true);
                    }
                    setSelected({...selected, [node.uuid]: isChecked});
                    if (isChecked) {
                      setSelectedCount(selectedCount + 1);
                    } else {
                      setSelectedCount(Math.max(selectedCount - 1, 0));
                    }
                  }}
                />
              </div>
              <div className={stylesAntd.buttonSpacing}></div>
              {!hideDeleteFileButton && (
                <Button
                  key={`delete-${node.uuid}`}
                  aria-label="Delete"
                  type="text"
                  size="small"
                  icon={!pneuInsightsModel && <DeleteOutlined style={{fontSize: '16px', color: '#006A63'}} />}
                  onClick={() => {
                    handleDeleteFile(node as ModelFileUploaded);
                    setSpecificFiles([getNodeIdentifier(node as ModelTreeNode)], false);
                  }}
                  {...tid('btn', 'delete', node.name)}
                />
              )}
            </div>
          ),
        node.uploaded && nodeHasTag(node, ModelFileTags.DELETE) && (
          <Button
            key={`undelete-${node.uuid}`}
            aria-label="Undelete"
            type="text"
            size="small"
            icon={<PageFirst16 />}
            onClick={() => unmarkDelete(node as ModelFileUploaded)}
            {...tid('btn', 'undelete', node.name)}
          />
        ),
        !node.uploaded && (
          <>
            <div
              className={stylesAntd.buttonContainer}
              key={`upload-actions-${getNodeIdentifier(node as ModelTreeNode)}`}
            >
              <div className={stylesAntd.uploadCheckBoxContainer}>
                <Checkbox
                  key={`selected-${getNodeIdentifier(node as ModelTreeNode)}`}
                  checked={selected[getNodeIdentifier(node as ModelTreeNode)] || false}
                  onChange={(event) => {
                    const isChecked = event.target.checked;
                    // If unchecking any file, trigger unSelectAll to notify parent
                    if (!isChecked) {
                      setUnSelectAll(true);
                    }
                    setSelected({...selected, [getNodeIdentifier(node as ModelTreeNode)]: isChecked});
                    if (isChecked) {
                      setSelectedCount(selectedCount + 1);
                    } else {
                      setSelectedCount(Math.max(selectedCount - 1, 0));
                    }
                  }}
                />
              </div>
              {!hideDeleteFileButton && (
                <Button
                  key={`remove-${node.uuid}`}
                  type="text"
                  size="small"
                  icon={!pneuInsightsModel && <DeleteOutlined style={{fontSize: '16px', color: '#006A63'}} />}
                  onClick={() => {
                    remove(node as ModelTreeNode);
                    setSpecificFiles([getNodeIdentifier(node as ModelTreeNode)], false);
                  }}
                  {...tid('btn', 'remove', node.name)}
                />
              )}
            </div>
          </>
        ),
      ].filter((button) => button);
    },
    [
      remove,
      unmarkDelete,
      handleDeleteFile,
      pneuInsightsModel,
      selectedCount,
      selected,
      setSpecificFiles,
      hideDeleteFileButton,
      setUnSelectAll,
      setSelected,
      setSelectedCount,
    ],
  );

  const form = useForm(new FormGroup({}));

  return (
    <>
      {theoremModel && (
        <div style={{justifyContent: 'center', display: 'flex', backgroundColor: '#f4f4f4'}}>
          <Tabs
            activeKey={tab}
            onChange={(activeKey: string) => setTab(activeKey)}
            centered
            items={[
              {key: 'theorem', label: 'THEOREM files'},
              {key: 'supporting', label: 'Supporting Documents'},
            ]}
          />
        </div>
      )}
      <div style={{marginTop: '2rem'}}>
        {tab === 'theorem' && theoremModel && THfiles && (
          <>
            <span style={{fontSize: '15px', fontWeight: 'bold'}}>THEOREM Files</span>
            <br />
            <br />
            <FileTreeTH isModelGlobal={globalModel} externalModel={externalModel} files={THfiles} editFlow={true} />
          </>
        )}
      </div>

      {(!theoremModel || (theoremModel && tab === 'supporting')) && (
        <>
          <DialogConfirmation
            open={!!confirmNodeDeletion}
            onClickYes={handleConfirmDeleteFile}
            onClickNo={handleDeclineDeleteFile}
            text={`${confirmNodeDeletion?.name || ''} is a main model file. Are you sure you want to delete it?`}
          />

          <div className={clsx(stylesAntd.panel, stylesAntd.panelMlmp, stylesAntd.fileManagerAntd)}>
            {customUploadArea
              ? customUploadArea({
                  add,
                  nodes,
                  handleAddFiles,
                  handleDrop,
                  fileInputRef,
                  folderInputRef,
                  isDirUploadAvailable,
                  selectedCount,
                  renderFileTagSelection: () =>
                    selectedCount > 1 ? (
                      <FileTagSelectionBoxAntd
                        selectedFileCount={selectedCount}
                        onClick={handleSaveFileTag}
                        onCancel={unSelectAllFiles}
                        setUnSelectAll={setUnSelectAll}
                        unSelectAll={unSelectAll}
                      />
                    ) : null,
                  renderFileTree: () =>
                    nodes && nodes.length > 0 ? (
                      <FileTreeAntd
                        renderButtons={renderButtons}
                        files={nodes}
                        handleFileTag={handleFileTag}
                        createFlow={createFlow}
                        onDrop={handleDrop}
                        webModel={webModel}
                        setSpecificFiles={setSpecificFiles}
                        unSelectAll={unSelectAll}
                        uploadFlow={uploadFlow}
                        cloneFlow={cloneFlow}
                        theoremModel={theoremModel}
                      />
                    ) : null,
                })
              : null}

            {!customUploadArea && selectedCount > 1 && (
              <FileTagSelectionBox
                selectedFileCount={selectedCount}
                onClick={handleSaveFileTag}
                onCancel={unSelectAllFiles}
                setUnSelectAll={setUnSelectAll}
                unSelectAll={unSelectAll}
              />
            )}
            {!customUploadArea && theoremModel && nodes && nodes.length > 0 && (
              <span style={{fontSize: '15px', fontWeight: 'bold', marginBottom: '2rem'}}>Supporting Documents</span>
            )}
            {!customUploadArea && nodes && nodes.length > 0 && (
              <FileTreeAntd
                renderButtons={renderButtons}
                files={nodes}
                handleFileTag={handleFileTag}
                createFlow={createFlow}
                onDrop={handleDrop}
                webModel={webModel}
                setSpecificFiles={setSpecificFiles}
                unSelectAll={unSelectAll}
                uploadFlow={uploadFlow}
                cloneFlow={cloneFlow}
                theoremModel={theoremModel}
              />
            )}
            <br />
            <br />
            <Form
              className=""
              FieldGroupProps={{strict: false}}
              group={form}
              onSubmit={() => onSubmit(model, nodes)}
              render={() => {
                return <>{buttons()}</>;
              }}
            />
          </div>
        </>
      )}
    </>
  );
};

export default FileManagerAntd;
